import { Alert as MantineAlert, type MantineColor } from "@mantine/core";
import {
  IconCircleCheckOutline18,
  IconCircleInfoOutline18,
  IconCircleXmarkOutline18,
  IconLightbulbOutline18,
  IconNoteOutline18,
  IconTriangleWarningOutline18,
} from "nucleo-ui-outline-18";
import { createElement, type ReactNode } from "react";

import type { Icon } from "../icons";

/**
 * AlertVariant is the set of callout styles the {% alert %} tag supports.
 */
type AlertVariant = "danger" | "info" | "note" | "success" | "tip" | "warning";

interface AlertConfig {
  color: MantineColor;
  icon: Icon;
}

/**
 * alertVariants maps each alert variant to its Mantine color and Nucleo icon.
 */
const alertVariants: Record<AlertVariant, AlertConfig> = {
  danger: { color: "red", icon: IconCircleXmarkOutline18 },
  info: { color: "blue", icon: IconCircleInfoOutline18 },
  note: { color: "gray", icon: IconNoteOutline18 },
  success: { color: "green", icon: IconCircleCheckOutline18 },
  tip: { color: "teal", icon: IconLightbulbOutline18 },
  warning: { color: "orange", icon: IconTriangleWarningOutline18 },
};

interface AlertProps {
  children?: ReactNode;
  title?: string;
  variant?: AlertVariant;
}

/**
 * Alert is the component the {% alert %} tag renders to: a Mantine `Alert`
 * coloured and iconed per variant, with an optional title.
 */
export const Alert = ({ children, title, variant = "info" }: AlertProps) => {
  const { color, icon } = alertVariants[variant] ?? alertVariants.info;

  return (
    <MantineAlert color={color} icon={createElement(icon, { size: 18 })} my="md" title={title}>
      {children}
    </MantineAlert>
  );
};
